import { useState, useEffect, useRef } from 'react';
import { PlusCircle, Pencil, X, Check, Server, RefreshCw, Radio, Wifi, WifiOff } from 'lucide-react';
import { apiFetch } from '../api/client';

interface ClientDatabase {
  id: number;
  name: string;
  db_id: number;
  company_ids: number[];
  area_ids: number[] | 'all';
  active: boolean;
  source: 'mqtt' | 'ei_analytic';
  notes: string | null;
  updated_at: string;
}

interface Gateway {
  id: number;
  gateway_serial: string;
  db_id: number;
  db_name: string;
  notes: string | null;
  last_seen_at: string | null;
  online: boolean;
  created_at: string;
}

interface SyncStatus {
  running: boolean;
  started_at: string | null;
  finished_at: string | null;
  exit_code: number | null;
  output: string[];
}

const EMPTY_FORM = {
  name: '',
  db_id: '',
  company_ids: '1',
  area_ids: 'all' as 'all' | string,
  area_mode: 'all' as 'all' | 'specific',
  source: 'mqtt' as 'mqtt' | 'ei_analytic',
  notes: '',
};

export function DatabaseManagement() {
  const [dbs, setDbs]           = useState<ClientDatabase[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState<number | null>(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [sync, setSync]         = useState<SyncStatus | null>(null);
  const [syncing, setSyncing]   = useState(false);
  const [showLog, setShowLog]   = useState(false);
  const syncPollRef             = useRef<ReturnType<typeof setInterval> | null>(null);
  const logEndRef               = useRef<HTMLDivElement>(null);

  // Gateways
  const [gateways, setGateways]         = useState<Gateway[]>([]);
  const [gwForm, setGwForm]             = useState({ serial: '', db_id: '', notes: '' });
  const [gwFormOpen, setGwFormOpen]     = useState(false);
  const [gwSaving, setGwSaving]         = useState(false);
  const [gwError, setGwError]           = useState('');

  const loadGateways = () =>
    apiFetch<Gateway[]>('/admin/gateways').then(d => setGateways(d ?? [])).catch(() => {});

  const load = () => {
    setLoading(true);
    apiFetch<ClientDatabase[]>('/admin/databases')
      .then(data => setDbs(data ?? []))
      .catch(() => setError('Failed to load databases'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); loadGateways(); }, []);

  // Fetch sync status once on mount so we can show last-run state
  useEffect(() => {
    apiFetch<SyncStatus>('/admin/sync-tree/status').then(s => { if (s) setSync(s); }).catch(() => {});
  }, []);

  // Auto-scroll log to bottom when new output arrives
  useEffect(() => {
    if (showLog) logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sync?.output?.length, showLog]);

  // Poll while running; clear when done
  useEffect(() => {
    if (sync?.running) {
      syncPollRef.current = setInterval(() => {
        apiFetch<SyncStatus>('/admin/sync-tree/status').then(s => {
          if (s) {
            setSync(s);
            if (!s.running && syncPollRef.current) {
              clearInterval(syncPollRef.current);
              syncPollRef.current = null;
              load(); // refresh DB list after sync
            }
          }
        }).catch(() => {});
      }, 2000);
    }
    return () => {
      if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; }
    };
  }, [sync?.running]);

  const triggerSync = async () => {
    setSyncing(true);
    setShowLog(true);
    try {
      await apiFetch('/admin/sync-tree', { method: 'POST' });
      const s = await apiFetch<SyncStatus>('/admin/sync-tree/status');
      if (s) setSync(s);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to start sync-tree');
    } finally {
      setSyncing(false);
    }
  };

  const saveGateway = async () => {
    if (!gwForm.serial.trim()) { setGwError('Enter a gateway serial.'); return; }
    if (!gwForm.db_id) { setGwError('Select a database.'); return; }
    setGwSaving(true); setGwError('');
    try {
      await apiFetch('/admin/gateways', {
        method: 'POST',
        body: JSON.stringify({
          gateway_serial: gwForm.serial.trim(),
          db_id: parseInt(gwForm.db_id, 10),
          notes: gwForm.notes.trim() || null,
        }),
      });
      setGwForm({ serial: '', db_id: '', notes: '' });
      setGwFormOpen(false);
      loadGateways();
    } catch (err: any) {
      setGwError(err?.message ?? 'Failed to register gateway.');
    } finally {
      setGwSaving(false);
    }
  };

  const deleteGateway = async (gw: Gateway) => {
    if (!confirm(`Remove gateway "${gw.gateway_serial}" from registry?`)) return;
    try {
      await apiFetch(`/admin/gateways/${gw.id}`, { method: 'DELETE' });
      loadGateways();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to remove gateway.');
    }
  };

  const resetForm = () => { setForm(EMPTY_FORM); setShowForm(false); setEditId(null); };

  const openEdit = (db: ClientDatabase) => {
    setEditId(db.id);
    setShowForm(true);
    const areaIsAll = db.area_ids === 'all' || (Array.isArray(db.area_ids) && db.area_ids.length === 0);
    setForm({
      name:      db.name,
      db_id:     String(db.db_id),
      company_ids: Array.isArray(db.company_ids) ? db.company_ids.join(', ') : '1',
      area_ids:  areaIsAll ? 'all' : (Array.isArray(db.area_ids) ? db.area_ids.join(', ') : ''),
      area_mode: areaIsAll ? 'all' : 'specific',
      source:    db.source ?? 'mqtt',
      notes:     db.notes ?? '',
    });
    setError(''); setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);

    const company_ids = form.company_ids.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    const area_ids    = form.area_mode === 'all' ? 'all'
      : form.area_ids.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));

    const body = {
      name:        form.name.trim(),
      db_id:       parseInt(form.db_id, 10),
      company_ids,
      area_ids,
      source:      form.source,
      notes:       form.notes.trim() || null,
    };

    try {
      if (editId !== null) {
        await apiFetch(`/admin/databases/${editId}`, { method: 'PATCH', body: JSON.stringify(body) });
        setSuccess(`"${body.name}" updated.`);
      } else {
        await apiFetch('/admin/databases', { method: 'POST', body: JSON.stringify(body) });
        setSuccess(`"${body.name}" registered. It will be picked up on the next poll cycle.`);
      }
      resetForm();
      load();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save database');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (db: ClientDatabase) => {
    const action = db.active ? 'deactivate' : 're-activate';
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} "${db.name}"?`)) return;
    try {
      if (db.active) {
        await apiFetch(`/admin/databases/${db.id}`, { method: 'DELETE' });
      } else {
        await apiFetch(`/admin/databases/${db.id}`, { method: 'PATCH', body: JSON.stringify({ active: true }) });
      }
      setSuccess(`"${db.name}" ${db.active ? 'deactivated' : 're-activated'}.`);
      load();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update database');
    }
  };

  const syncLabel = () => {
    if (!sync) return null;
    if (sync.running) return <span className="text-blue-600 text-xs">Running…</span>;
    if (sync.finished_at) {
      const ok = sync.exit_code === 0;
      return (
        <span className={`text-xs ${ok ? 'text-green-600' : 'text-red-500'}`}>
          {ok ? 'Completed' : `Failed (exit ${sync.exit_code})`}
          {' · '}
          <button onClick={() => setShowLog(l => !l)} className="underline">
            {showLog ? 'hide log' : 'show log'}
          </button>
        </span>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-gray-900">Database Registry</h2>
          <p className="text-gray-500 text-sm mt-1">Manage monitored EI-Analytic databases. Changes take effect on the next poll cycle.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={triggerSync}
              disabled={syncing || sync?.running}
              title="Re-sync machine hierarchy from EI-Analytic"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${sync?.running ? 'animate-spin text-blue-500' : ''}`} />
              Sync Tree
            </button>
            <span className="min-w-[120px]">{syncLabel()}</span>
          </div>
          {!showForm && (
            <button
              onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_FORM); setError(''); setSuccess(''); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <PlusCircle className="w-4 h-4" />
              Add Database
            </button>
          )}
        </div>
      </div>

      {/* Sync log */}
      {showLog && sync && sync.output.length > 0 && (
        <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400 font-mono">sync-tree output</span>
            <button onClick={() => setShowLog(false)} className="text-gray-500 hover:text-gray-300">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto font-mono text-xs text-green-400 space-y-0.5">
            {sync.output.map((line, i) => <div key={i}>{line || ' '}</div>)}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {error   && <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="px-4 py-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-gray-900 mb-5">{editId ? 'Edit Database' : 'Register New Database'}</h3>

          {!editId && (
            <div className="mb-5 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
              The <strong>Name</strong> must match the database name exactly as it appears in the EI-Analytic login response (case-sensitive).
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Database Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Berry"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">Must match EI-Analytic exactly</p>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">EI-Analytic DB ID <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  required
                  value={form.db_id}
                  onChange={e => setForm(f => ({ ...f, db_id: e.target.value }))}
                  placeholder="e.g. 699"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">Found in EI-Analytic login response</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Company IDs to sync</label>
                <input
                  type="text"
                  value={form.company_ids}
                  onChange={e => setForm(f => ({ ...f, company_ids: e.target.value }))}
                  placeholder="1, 2, 3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">Comma-separated EI-Analytic company IDs</p>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Areas to sync</label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                    <input type="radio" value="all" checked={form.area_mode === 'all'} onChange={() => setForm(f => ({ ...f, area_mode: 'all' }))} />
                    All areas
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                    <input type="radio" value="specific" checked={form.area_mode === 'specific'} onChange={() => setForm(f => ({ ...f, area_mode: 'specific' }))} />
                    Specific IDs
                  </label>
                </div>
                {form.area_mode === 'specific' && (
                  <input
                    type="text"
                    value={form.area_ids === 'all' ? '' : form.area_ids}
                    onChange={e => setForm(f => ({ ...f, area_ids: e.target.value }))}
                    placeholder="1, 2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Data Source</label>
              <div className="flex gap-3">
                {(['mqtt', 'ei_analytic'] as const).map(s => (
                  <label key={s} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="radio" value={s} checked={form.source === s}
                      onChange={() => setForm(f => ({ ...f, source: s }))} />
                    {s === 'mqtt' ? 'MQTT only' : 'EI-Analytic (+ API polling)'}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional — client name, site, contact..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-60"
              >
                {saving ? 'Saving…' : editId ? 'Save Changes' : 'Register Database'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : dbs.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No databases registered.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Name</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">DB ID</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Source</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Companies</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Areas</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Status</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Notes</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {dbs.map(db => (
                <tr key={db.id} className={`border-b border-gray-50 transition-colors ${db.active ? 'hover:bg-gray-50' : 'bg-gray-50 opacity-60'}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Server className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="font-medium text-gray-900">{db.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-gray-600 text-xs">{db.db_id}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      db.source === 'ei_analytic'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-purple-50 text-purple-700'
                    }`}>
                      {db.source === 'ei_analytic' ? 'EI-Analytic' : 'MQTT'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {Array.isArray(db.company_ids) ? db.company_ids.join(', ') : '—'}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {db.area_ids === 'all' || !db.area_ids || (Array.isArray(db.area_ids) && db.area_ids.length === 0)
                      ? <span className="text-gray-400 text-xs">All</span>
                      : (Array.isArray(db.area_ids) ? db.area_ids.join(', ') : String(db.area_ids))
                    }
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      db.active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {db.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-400 text-xs max-w-xs truncate">{db.notes ?? '—'}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(db)}
                        className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors group"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                      </button>
                      <button
                        onClick={() => toggleActive(db)}
                        className={`p-1.5 rounded-lg transition-colors group ${db.active ? 'hover:bg-red-50' : 'hover:bg-green-50'}`}
                        title={db.active ? 'Deactivate' : 'Re-activate'}
                      >
                        {db.active
                          ? <X className="w-4 h-4 text-gray-400 group-hover:text-red-500" />
                          : <Check className="w-4 h-4 text-gray-400 group-hover:text-green-600" />
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Gateway Registry */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-gray-800 font-medium">Gateways</h3>
            <p className="text-gray-400 text-xs mt-0.5">Assign MQTT gateways to databases. Online = message received within 15 minutes.</p>
          </div>
          <button
            onClick={() => { setGwFormOpen(true); setGwError(''); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <PlusCircle className="w-4 h-4" />
            Add Gateway
          </button>
        </div>

        {gwFormOpen && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h4 className="text-gray-800 text-sm font-medium mb-4">Register Gateway</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Gateway Serial <span className="text-red-400">*</span></label>
                <div className="relative">
                  <Radio className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={gwForm.serial}
                    onChange={e => setGwForm(f => ({ ...f, serial: e.target.value }))}
                    placeholder="e.g. PhantomGW-589245234"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Database <span className="text-red-400">*</span></label>
                <select
                  value={gwForm.db_id}
                  onChange={e => setGwForm(f => ({ ...f, db_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select database…</option>
                  {dbs.filter(d => d.active).map(d => (
                    <option key={d.db_id} value={d.db_id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  type="text"
                  value={gwForm.notes}
                  onChange={e => setGwForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Site, location…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            {gwError && <p className="text-red-600 text-xs mt-3">{gwError}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={saveGateway} disabled={gwSaving}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-60">
                {gwSaving ? 'Saving…' : 'Register'}
              </button>
              <button onClick={() => { setGwFormOpen(false); setGwError(''); }}
                className="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {gateways.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No gateways registered.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Gateway Serial</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Database</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Last Seen</th>
                  <th className="text-left px-5 py-3 text-gray-600 font-medium">Notes</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {gateways.map(gw => (
                  <tr key={gw.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-5 py-3">
                      {gw.online
                        ? <span className="flex items-center gap-1.5 text-green-600 text-xs font-medium"><Wifi className="w-3.5 h-3.5" />Online</span>
                        : <span className="flex items-center gap-1.5 text-gray-400 text-xs"><WifiOff className="w-3.5 h-3.5" />{gw.last_seen_at ? 'Offline' : 'Never seen'}</span>
                      }
                    </td>
                    <td className="px-5 py-3 font-mono text-gray-800 text-xs">{gw.gateway_serial}</td>
                    <td className="px-5 py-3 text-gray-600">{gw.db_name ?? `db_id ${gw.db_id}`}</td>
                    <td className="px-5 py-3 text-gray-400 text-xs">
                      {gw.last_seen_at ? new Date(gw.last_seen_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-gray-400 text-xs">{gw.notes ?? '—'}</td>
                    <td className="px-5 py-3">
                      <button onClick={() => deleteGateway(gw)}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors group" title="Remove">
                        <X className="w-4 h-4 text-gray-300 group-hover:text-red-500" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
